/**
 * Canonical synthetic blank record card (cardless field sketch) — frontend
 * mirror of `backend/services/field_blank_card.py` (Wave G Task A).
 *
 * Spec: docs/superpowers/specs/2026-07-23-cardless-field-sketch-design.md
 *
 * `BLANK_CARD_PDF_B64` and `BLANK_CARD_SHA256` below are copied VERBATIM from
 * the backend module — NEVER re-generate or re-derive them here. The design's
 * core invariant (client and server agree on the exact same sha256 for a
 * field-blank card's version 1, so delta sync never sees a diff and never
 * re-downloads or drops marks) only holds if both sides embed the SAME frozen
 * bytes. Two "equivalent" PDF outputs are NOT byte-identical — PDF libraries
 * (fitz included) embed a random-looking trailer `/ID` on every save — so the
 * backend froze one generation as the canonical literal; this module copies
 * that exact literal rather than trying to reproduce it independently.
 */

/** Frozen bytes — see the module doc comment. Never regenerate. */
export const BLANK_CARD_PDF_B64 =
  'JVBERi0xLjcKJcK1wrYKJSBXcml0dGVuIGJ5IE11UERGIDEuMjcuMgoKMSAwIG9iago8PC9UeXBl' +
  'L0NhdGFsb2cvUGFnZXMgMiAwIFIvSW5mbzw8L1Byb2R1Y2VyKE11UERGIDEuMjcuMik+Pj4+CmVu' +
  'ZG9iagoKMiAwIG9iago8PC9UeXBlL1BhZ2VzL0NvdW50IDEvS2lkc1s0IDAgUl0+PgplbmRvYmoK' +
  'CjMgMCBvYmoKPDw+PgplbmRvYmoKCjQgMCBvYmoKPDwvVHlwZS9QYWdlL01lZGlhQm94WzAgMCA2' +
  'MTIgNzkyXS9Sb3RhdGUgMC9SZXNvdXJjZXMgMyAwIFIvUGFyZW50IDIgMCBSPj4KZW5kb2JqCgp4' +
  'cmVmCjAgNQowMDAwMDAwMDAwIDY1NTM1IGYgCjAwMDAwMDAwNDIgMDAwMDAgbiAKMDAwMDAwMDEy' +
  'MCAwMDAwMCBuIAowMDAwMDAwMTcyIDAwMDAwIG4gCjAwMDAwMDAxOTMgMDAwMDAgbiAKCnRyYWls' +
  'ZXIKPDwvU2l6ZSA1L1Jvb3QgMSAwIFIvSURbPEMzQTU2QkMzOTlDMkI0QzJCNzREMjgwMTExQzJC' +
  'RkMzPjxCQUVDNTdGOEY5RDRBODkwNTE0OEM5MTdFQUE0RUE5MT5dPj4Kc3RhcnR4cmVmCjI4NAol' +
  'JUVPRgo=';

/** Frozen ALONGSIDE the bytes, independently of them (see the backend module
 * for why: a separately-recorded literal makes the integrity test meaningful
 * rather than tautological). */
export const BLANK_CARD_SHA256 =
  '5c22f18b01e7f3c5167b193b0f2a25a07ccd703fd9f9539a5859d4a1ec3e37b8';

/** Registry-doc / card-meta tag distinguishing a synthetic field-blank card
 * from a real scanned/imported one. Mirrors backend's `FIELD_BLANK_SOURCE`. */
export const FIELD_BLANK_SOURCE = 'field_blank' as const;

/**
 * Decode the canonical blank PDF's base64 literal into raw bytes. `atob` is
 * available globally in every browser and in the vitest/jsdom test
 * environment — no base64 dependency needed.
 */
export function blankCardPdfBytes(): Uint8Array {
  const binary = atob(BLANK_CARD_PDF_B64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
